import { useState } from "react";

import { analyseMmsi } from "@nav-games/comms";

/// MMSI decoder — type any 9-digit MMSI and the decoder identifies
/// the station category, extracts the MID, and looks up the country.
/// Used in the Reference comms section.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 18,
  letterSpacing: "0.1em",
};

const CATEGORY_LABEL: Record<string, string> = {
  ship: "Ship station",
  "ship-group": "Ship group",
  "coast-station": "Coast (shore) station",
  "sar-aircraft": "SAR aircraft",
  "ais-aton": "AIS Aid-to-Navigation",
  "auxiliary-craft": "Auxiliary craft",
  epirb: "EPIRB / PLB beacon",
  unknown: "Unknown / invalid",
};

const PRESETS: ReadonlyArray<{ label: string; mmsi: string }> = [
  { label: "UK ship", mmsi: "232123456" },
  { label: "UK coast station (Solent CG)", mmsi: "002320014" },
  { label: "SAR helicopter", mmsi: "111232456" },
  { label: "AIS-AtoN buoy", mmsi: "992320123" },
  { label: "Tender / RIB", mmsi: "982321234" },
  { label: "AIS EPIRB", mmsi: "974123456" },
];

export function MmsiDecoder() {
  const [mmsi, setMmsi] = useState("232123456");
  const result = analyseMmsi(mmsi);

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>MMSI decoder</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 10 }}>
        Type any 9-digit Maritime Mobile Service Identity — the decoder
        breaks it into category + MID + country. Recognises ships,
        coast stations, SAR aircraft, AIS-AtoN, ship groups, auxiliary
        craft and EPIRB beacons.
      </p>

      <input
        type="text"
        value={mmsi}
        onChange={(e) => setMmsi(e.target.value)}
        inputMode="numeric"
        pattern="\d{9}"
        maxLength={9}
        style={inputStyle}
        aria-label="MMSI"
      />

      <div
        style={{
          display: "flex",
          gap: 6,
          marginTop: 8,
          flexWrap: "wrap",
        }}
      >
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            className="secondary"
            onClick={() => setMmsi(p.mmsi)}
            style={{ fontSize: 12, padding: "6px 10px" }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {result.valid ? (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--accent)",
            background: "var(--accent-soft)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 8,
            }}
          >
            <div>
              <div className="small muted">Category</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {CATEGORY_LABEL[result.category]}
              </div>
            </div>
            {result.mid && (
              <div>
                <div className="small muted">MID</div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}
                >
                  {result.mid}
                </div>
              </div>
            )}
            {result.country && (
              <div>
                <div className="small muted">Country</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {result.country}
                </div>
              </div>
            )}
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            {result.description}
          </p>
        </div>
      ) : (
        <p
          className="small"
          style={{
            color: "var(--accent)",
            margin: "10px 0 0",
            fontWeight: 600,
          }}
        >
          MMSI must be exactly 9 digits (no spaces or letters).
        </p>
      )}
    </div>
  );
}
